import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { OrgAdminApi } from '../../services/api/OrgAdminApi';
import { OrganizationApi } from '../../services/ApiService';

const ROLE_OPTIONS = [
  { value: 'org_member', label: 'Member' },
  { value: 'course_admin', label: 'Course Admin' },
  { value: 'org_admin', label: 'Org Admin' },
];

/**
 * Settings > Organizations > Members.
 * Route: /settings/organizations/:organizationId/members
 * Shows all members of an org with role management + add-member form.
 * Uses OrgAdminApi endpoints (global admins bypass @OrgAdminOnly).
 */
function OrganizationMembersPage() {
  const { organizationId } = useParams();
  const [orgName, setOrgName] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', orgRole: 'org_member' });
  const [formError, setFormError] = useState(null);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await OrgAdminApi.getMembers(organizationId);
      if (!resp.ok) { setError(`Failed to load members (${resp.status})`); return; }
      setMembers(await resp.json());
    } catch (e) {
      setError(e?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadMembers();
    // Load org name
    OrganizationApi.getOrganization(organizationId).then(async resp => {
      if (resp.ok) {
        const org = await resp.json();
        setOrgName(org.name || org.organizationName || `Org #${organizationId}`);
      }
    }).catch(() => {});
  }, [organizationId, loadMembers]);

  const handleChangeRole = async (userId, orgRole) => {
    setError(null);
    const resp = await OrgAdminApi.updateMemberRole(organizationId, userId, orgRole);
    if (!resp.ok) { setError(`Failed to update role (${resp.status})`); return; }
    loadMembers();
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addForm.email.trim()) return;
    setAdding(true);
    setFormError(null);
    try {
      const resp = await OrgAdminApi.addMember(organizationId, addForm.email.trim(), addForm.orgRole);
      if (resp.status === 404) { setFormError('No user found with that email.'); return; }
      if (resp.status === 409) { setFormError('User is already a member of this organization.'); return; }
      if (!resp.ok) { setFormError(`Failed to add member (${resp.status})`); return; }
      setAddForm({ email: '', orgRole: 'org_member' });
      setShowForm(false);
      loadMembers();
    } catch (err) {
      setFormError(err?.message || 'Network error');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName || 'this user'} from ${orgName || 'this organization'}?`)) return;
    setError(null);
    try {
      const resp = await OrgAdminApi.removeMember(organizationId, userId);
      if (!resp.ok) { setError(`Failed to remove member (${resp.status})`); return; }
      loadMembers();
    } catch (e) {
      setError(e?.message || 'Network error');
    }
  };

  return (
    <div className="settings-page-content">
      <div className="settings-page-header">
        <h2>
          <Link to="/settings/organizations" style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.9em' }}>
            Organizations
          </Link>
          {' › '}
          {orgName || `Org #${organizationId}`}
          {' › Members'}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Link to={`/organization/${organizationId}/admin`}>
            <button type="button">Open Org Dashboard →</button>
          </Link>
          <button type="button" onClick={() => setShowForm(f => !f)}>
            {showForm ? 'Cancel' : '+ Add Member'}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {formError && <div className="error-message">{formError}</div>}

      {showForm && (
        <form onSubmit={handleAdd} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', marginBottom: '1rem', padding: '1rem', background: 'var(--light-color, #f8f9fa)', borderRadius: '8px', border: '1px solid var(--border-color, #e1e5e9)' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '4px' }}>Email</label>
            <input
              type="email"
              placeholder="user@example.edu"
              value={addForm.email}
              onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
              required
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '4px' }}>Role</label>
            <select value={addForm.orgRole} onChange={e => setAddForm(f => ({ ...f, orgRole: e.target.value }))}>
              {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <button type="submit" disabled={adding}>{adding ? 'Adding…' : 'Add'}</button>
        </form>
      )}

      {loading ? (
        <div className="loading-state"><div className="loading-spinner" /><p>Loading members…</p></div>
      ) : members.length === 0 ? (
        <div className="empty-state"><h3>No members</h3><p>Add a member to this organization.</p></div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Org Role</th>
              <th>Status</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map(m => {
              const uid = m.userId || m.user_id;
              return (
                <tr key={m.membershipId || uid}>
                  <td data-label="User">
                    <strong>{m.userName || m.firstName || `User ${uid}`}</strong>
                  </td>
                  <td data-label="Email">{m.email || '—'}</td>
                  <td data-label="Role">
                    <select
                      defaultValue={m.orgRole || 'org_member'}
                      onChange={e => handleChangeRole(uid, e.target.value)}
                    >
                      {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </td>
                  <td data-label="Status">{m.status || '—'}</td>
                  <td data-label="Joined">{m.joinedAt ? new Date(m.joinedAt).toLocaleDateString() : '—'}</td>
                  <td data-label="Actions">
                    <Link to={`/settings/users/${uid}`}>Edit User</Link>
                    {' | '}
                    <button
                      type="button"
                      onClick={() => handleRemove(uid, m.userName || m.firstName || m.email)}
                      style={{ background: 'none', border: 'none', color: 'var(--danger-color, #dc3545)', cursor: 'pointer', padding: 0, textDecoration: 'underline', fontSize: 'inherit' }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="info-panel">
        <h4>About Organization Members</h4>
        <p>
          Members belong to this organization with one of three roles:
          <strong> Org Admin</strong> (full org visibility + management),
          <strong> Course Admin</strong> (administers courses they teach), or
          <strong> Member</strong> (student-level).
          Add existing platform users by email, then assign the appropriate role.
        </p>
      </div>
    </div>
  );
}

export default OrganizationMembersPage;
